import {
  Children,
  createContext,
  isValidElement,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'
import { tab, tabActive, tabIndicator, tabsContainer } from './Tabs.css'

type TabsContextValue = {
  activeTab: string
  setActiveTab: (name: string) => void
}

const TabsContext = createContext<TabsContextValue | null>(null)

type TabsProps = {
  children: ReactNode
  defaultValue?: string
  /** Controlled active tab (URL-driven pages); the indicator then follows it without remounting. */
  value?: string
  onChange?: (name: string) => void
}

export const Tabs = ({
  children,
  defaultValue,
  value,
  onChange
}: TabsProps) => {
  const tabsRef = useRef<HTMLDivElement>(null)
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 })

  const firstTabChild = Children.toArray(children).find(
    (child) => isValidElement(child) && child.type === Tab
  )
  const firstTabName =
    isValidElement(firstTabChild) && firstTabChild.type === Tab
      ? firstTabChild.props.name
      : ''

  const [internalTab, setInternalTab] = useState(defaultValue ?? firstTabName)
  const activeTab = value ?? internalTab

  const handleTabChange = (name: string) => {
    if (value === undefined) {
      setInternalTab(name)
    }
    onChange?.(name)
  }

  useEffect(() => {
    if (!tabsRef.current) return
    const activeTabEl = tabsRef.current.querySelector(
      `[data-tab="${activeTab}"]`
    ) as HTMLButtonElement | null
    if (activeTabEl) {
      setIndicatorStyle({
        left: activeTabEl.offsetLeft,
        width: activeTabEl.offsetWidth
      })
    }
  }, [activeTab])

  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab: handleTabChange }}>
      <div className={tabsContainer} ref={tabsRef}>
        {children}
        <div
          className={tabIndicator}
          style={{ left: indicatorStyle.left, width: indicatorStyle.width }}
        />
      </div>
    </TabsContext.Provider>
  )
}

type TabProps = {
  name: string
  children: ReactNode
}

export const Tab = ({ name, children }: TabProps) => {
  const context = useContext(TabsContext)
  if (!context) throw new Error('Tab must be used within Tabs')

  const { activeTab, setActiveTab } = context

  return (
    <button
      type="button"
      data-tab={name}
      onClick={() => setActiveTab(name)}
      className={`${tab} ${activeTab === name ? tabActive : ''}`}
    >
      {children}
    </button>
  )
}
